/**
 * DesktopShell: a browser/monitor frame that mirrors the real ConfirmPH
 * Business layout (top bar with logo + business chip + user badge, left
 * sidebar with grouped nav items).  Used for all internal Business-side
 * demo screens (Notepad, Dashboard, Drafts, Bookings, Vehicles, Warnings,
 * Follow-ups, Deposit records, activity history, etc.).
 *
 * The customer-side channel apps (Messenger, WhatsApp, SMS, Phone) stay
 * inside the phone-shaped DeviceShell so the promoter can visually show
 * where a message actually lands.
 *
 * Sandbox rules unchanged: no network calls, no writes, everything is
 * demo state passed in as props.
 */

import {
  LayoutDashboard, CalendarDays, ClipboardList, Car, AlertTriangle,
  ClipboardCheck, Users, Wrench, Activity, Settings, Bell, LogOut,
  Menu, ArrowLeft, MonitorSmartphone,
} from "lucide-react";
import { DemoBadge } from "../DemoPanels";

// Nav mirrors the real DashboardLayout so managers instantly recognise it.
// "active" is decided by which internal app is on-screen; disabled items
// carry a hint that keeps the scenario contained but visually complete.
const NAV_GROUPS = [
  {
    key: "daily",
    title: "Daily",
    items: [
      { id: "dashboard",   label: "Today",         icon: LayoutDashboard, actives: ["dashboard"] },
      { id: "bookings",    label: "Bookings",      icon: CalendarDays,     actives: ["bookings", "booking-detail", "edit-booking", "find-booking", "activity-timeline", "review-invite", "review-public"] },
      { id: "intake",      label: "Booking Intake", icon: ClipboardList,   actives: ["notepad"] },
      { id: "vehicles",    label: "Vehicles",      icon: Car,              actives: ["vehicles", "vehicle-form", "vehicle-detail", "damage-incident", "maintenance-form"] },
      { id: "warnings",    label: "Warnings",      icon: AlertTriangle,    actives: ["warnings"] },
      { id: "followups",   label: "Tasks",         icon: ClipboardCheck,   actives: ["reminders"] },
      { id: "activity",    label: "Activity",      icon: Activity,         actives: ["activity"] },
    ],
  },
  {
    key: "manage",
    title: "Manage",
    items: [
      { id: "employees",   label: "Employees",     icon: Users,   actives: ["employees", "employee-form", "employee-performance"] },
      { id: "staffavail",  label: "Staff on Duty", icon: Users,   actives: ["staff-availability"] },
      { id: "checklists",  label: "Daily Checklists", icon: ClipboardCheck, actives: ["checklists", "checklist-run"] },
      { id: "maintenance", label: "Maintenance",   icon: Wrench,  actives: [] },
      { id: "settings",    label: "Settings",      icon: Settings, actives: [] },
    ],
  },
];

export default function DesktopShell({
  children, business, activeApp, onSwitchApp, onExit,
}) {
  return (
    <div data-testid="desktop-shell"
         className="mx-auto w-full max-w-[1180px] rounded-2xl border-[6px] border-slate-800 bg-slate-900 shadow-2xl overflow-hidden">
      {/* Browser-window chrome so it visibly reads as "on a computer" */}
      <BrowserChrome />

      {/* Actual ConfirmPH Business look-alike */}
      <div className="bg-[#F8FAFC] min-h-[560px]">
        <TopBar business={business} onExit={onExit} />
        <div className="flex">
          <Sidebar activeApp={activeApp} onSwitchApp={onSwitchApp} />
          <main data-testid="desktop-main"
                className="flex-1 min-w-0 p-4 sm:p-6 max-h-[560px] overflow-y-auto">
            {children}
          </main>
        </div>
      </div>
    </div>
  );
}

function BrowserChrome() {
  return (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 text-slate-300">
      <span className="w-2.5 h-2.5 rounded-full bg-rose-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" />
      <div className="flex-1 mx-3 text-[11px] font-mono bg-slate-700/60 rounded-md px-3 py-1 truncate">
        confirmph.app/app · <span className="text-emerald-300">Business Dashboard (demo)</span>
      </div>
      <DemoBadge>DEMO</DemoBadge>
    </div>
  );
}

function TopBar({ business, onExit }) {
  return (
    <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-md border-b border-slate-200 px-4 sm:px-5 h-14 flex items-center gap-3">
      <button className="lg:hidden p-1.5 rounded-md hover:bg-slate-100"
              data-testid="desktop-sidebar-toggle" title="Sidebar (demo)">
        <Menu className="w-4 h-4 text-slate-600" />
      </button>
      <div className="flex items-center gap-2.5">
        <img src="/brand/confirmph-logo.png" alt=""
             onError={(e) => { e.currentTarget.style.display = "none"; }}
             className="h-7 w-auto" />
        <div className="leading-tight border-l border-slate-200 pl-2.5 hidden sm:block">
          <div className="text-[9px] uppercase tracking-[0.18em] text-slate-400 font-semibold">Business</div>
          <div className="text-sm font-medium text-slate-800 truncate max-w-[220px]"
                data-testid="desktop-business-name">{business}</div>
        </div>
      </div>
      <div className="flex-1" />
      <button className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#1B4332] text-white text-xs font-medium"
              title="Not part of this demo">
        + Add booking
      </button>
      <Bell className="w-4 h-4 text-slate-500" />
      <div className="text-right hidden sm:block leading-tight">
        <div className="text-xs font-medium text-slate-900">Juan Dela Cruz</div>
        <div className="text-[10px] text-slate-500">Owner · demo</div>
      </div>
      {onExit && (
        <button onClick={onExit}
                data-testid="desktop-back-to-intro"
                className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500"
                title="Back to intro / phone view">
          <ArrowLeft className="w-4 h-4" />
        </button>
      )}
      <button className="p-1.5 rounded-md hover:bg-slate-100 text-slate-500" title="Sign out (demo)">
        <LogOut className="w-4 h-4" />
      </button>
    </header>
  );
}

function Sidebar({ activeApp, onSwitchApp }) {
  return (
    <aside data-testid="desktop-sidebar"
           className="hidden md:block w-56 bg-white border-r border-slate-200 min-h-[506px] max-h-[506px] overflow-y-auto py-3">
      <nav className="px-2 space-y-4">
        {NAV_GROUPS.map((g) => (
          <div key={g.key}>
            <div className="px-3 pt-1 pb-1.5 text-[9px] uppercase tracking-[0.2em] text-slate-400 font-semibold">
              {g.title}
            </div>
            <div className="space-y-0.5">
              {g.items.map((it) => {
                const active = it.actives.includes(activeApp);
                const clickable = it.actives.length > 0;
                return (
                  <button key={it.id}
                          disabled={!clickable}
                          onClick={() => clickable && onSwitchApp(it.actives[0])}
                          data-testid={`desktop-nav-${it.id}`}
                          title={clickable ? "" : "Not part of this demo scenario"}
                          className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs transition-colors text-left ${
                            active ? "bg-[#1B4332] text-white font-medium"
                                    : clickable
                                    ? "text-slate-700 hover:bg-slate-100"
                                    : "text-slate-400 cursor-not-allowed"
                          }`}>
                    <it.icon className="w-4 h-4 flex-shrink-0" strokeWidth={1.8} />
                    <span className="truncate">{it.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <div className="mt-4 mx-3 rounded-md bg-emerald-50 border border-emerald-200 p-2 text-[10px] text-emerald-900 flex items-start gap-1.5">
        <MonitorSmartphone className="w-3 h-3 mt-0.5 flex-shrink-0" />
        <span>Business tools open on the office computer.</span>
      </div>
    </aside>
  );
}
